<template>
  <div class="min-h-screen flex items-center justify-center bg-muted/30 py-12 px-4">
    <div class="w-full max-w-sm space-y-6">

      <!-- Header -->
      <div class="text-center space-y-1.5">
        <h1 class="text-2xl font-semibold tracking-tight">Reset your password</h1>
        <p class="text-sm text-muted-foreground">
          <template v-if="currentStep === 'email'">Enter your email to receive a verification code</template>
          <template v-else-if="currentStep === 'verify'">Check your inbox for the code</template>
          <template v-else-if="currentStep === 'reset'">Create your new password</template>
          <template v-else>You're all set</template>
        </p>
      </div>

      <!-- Card -->
      <div class="rounded-xl border border-border bg-card shadow-sm">

        <!-- Step 1: Email -->
        <div v-if="currentStep === 'email'" class="p-6 space-y-4">
          <form @submit.prevent="handleSubmitEmail" class="space-y-4">
            <div class="space-y-1.5">
              <label for="email" class="text-sm font-medium leading-none">Email address</label>
              <input
                v-model="email"
                id="email"
                type="email"
                class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                placeholder="you@example.com"
                required
                autocomplete="email"
              />
              <p v-if="errors.email" class="text-xs text-destructive">{{ errors.email }}</p>
            </div>

            <div v-if="formError" class="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive flex items-start gap-2">
              <i class="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
              <span>{{ formError }}</span>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin text-xs"></i>
              {{ loading ? 'Sending...' : 'Send verification code' }}
            </button>

            <div class="text-center">
              <router-link to="/login" class="text-sm text-primary hover:underline">Back to login</router-link>
            </div>
          </form>
        </div>

        <!-- Step 2: Verify -->
        <div v-if="currentStep === 'verify'" class="p-6 space-y-4">
          <div class="text-center space-y-2">
            <div class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary mb-1">
              <i class="fas fa-key text-lg"></i>
            </div>
            <p class="text-sm text-muted-foreground">
              We sent a code to <span class="font-medium text-foreground">{{ email }}</span>
            </p>
          </div>

          <div class="rounded-md bg-amber-50 border border-amber-200 px-3 py-2.5 text-xs text-amber-800 flex items-start gap-2">
            <i class="fas fa-exclamation-triangle mt-0.5 shrink-0 text-amber-500"></i>
            <span>Can't find the email? Check your <strong>spam or junk folder</strong> — it may have been filtered there.</span>
          </div>

          <form @submit.prevent="handleVerifyCode" class="space-y-4">
            <div class="space-y-1.5">
              <label for="code" class="text-sm font-medium leading-none">Verification code</label>
              <input
                v-model="verificationCode"
                id="code"
                type="text"
                class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                placeholder="Enter the code"
                required
              />
              <p v-if="errors.code" class="text-xs text-destructive">{{ errors.code }}</p>
            </div>

            <div v-if="formError" class="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive flex items-start gap-2">
              <i class="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
              <span>{{ formError }}</span>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin text-xs"></i>
              {{ loading ? 'Verifying...' : 'Verify code' }}
            </button>

            <div class="text-center space-y-2">
              <button
                type="button"
                @click.prevent="handleResendCode"
                :disabled="loading || resendCountdown > 0"
                class="text-sm text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {{ resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend code' }}
              </button>
              <br />
              <router-link to="/login" class="text-sm text-muted-foreground hover:text-foreground">Back to login</router-link>
            </div>
          </form>
        </div>

        <!-- Step 3: Reset password -->
        <div v-if="currentStep === 'reset'" class="p-6 space-y-4">
          <div class="text-center space-y-1">
            <div class="inline-flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600 mb-1">
              <i class="fas fa-unlock-alt text-lg"></i>
            </div>
            <p class="text-sm text-muted-foreground">Create a strong new password</p>
          </div>

          <form @submit.prevent="handleResetPassword" class="space-y-4">
            <div class="space-y-1.5">
              <label for="newPassword" class="text-sm font-medium leading-none">New password</label>
              <div class="relative">
                <input
                  v-model="newPassword"
                  id="newPassword"
                  :type="showPassword ? 'text' : 'password'"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors pr-10"
                  placeholder="Min. 8 characters with uppercase + number"
                  required
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  tabindex="-1"
                >
                  <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-xs"></i>
                </button>
              </div>
              <p v-if="errors.newPassword" class="text-xs text-destructive">{{ errors.newPassword }}</p>
            </div>

            <div class="space-y-1.5">
              <label for="confirmPassword" class="text-sm font-medium leading-none">Confirm new password</label>
              <div class="relative">
                <input
                  v-model="confirmPassword"
                  id="confirmPassword"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors pr-10"
                  placeholder="Repeat new password"
                  required
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors"
                  tabindex="-1"
                >
                  <i :class="showConfirmPassword ? 'fas fa-eye-slash' : 'fas fa-eye'" class="text-xs"></i>
                </button>
              </div>
              <p v-if="errors.confirmPassword" class="text-xs text-destructive">{{ errors.confirmPassword }}</p>
            </div>

            <div v-if="formError" class="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive flex items-start gap-2">
              <i class="fas fa-exclamation-circle mt-0.5 shrink-0"></i>
              <span>{{ formError }}</span>
            </div>

            <button
              type="submit"
              :disabled="loading"
              class="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              <i v-if="loading" class="fas fa-spinner fa-spin text-xs"></i>
              {{ loading ? 'Saving...' : 'Reset password' }}
            </button>
          </form>
        </div>

        <!-- Step 4: Success -->
        <div v-if="currentStep === 'success'" class="p-8 text-center space-y-4">
          <div class="inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
            <i class="fas fa-check text-2xl"></i>
          </div>
          <div class="space-y-1">
            <h3 class="text-lg font-semibold">Password reset!</h3>
            <p class="text-sm text-muted-foreground">You can now sign in with your new password.</p>
          </div>
          <router-link
            to="/login"
            class="inline-flex w-full items-center justify-center rounded-md bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Go to login
          </router-link>
        </div>
      </div>

      <p class="text-center text-xs text-muted-foreground">
        By proceeding, you agree to our
        <a href="#" class="text-primary hover:underline">Terms</a>
        and
        <a href="#" class="text-primary hover:underline">Privacy Policy</a>.
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import apiClient from '@/services/api';

export default {
  name: 'ForgotPasswordPage',
  setup() {
    // Keep the router for potential future use but don't assign to variable
    useRouter();

    // Form state
    const loading = ref(false);
    const formError = ref('');
    const errors = ref({
      email: '',
      code: '',
      newPassword: '',
      confirmPassword: ''
    });

    // Form steps: email -> verify -> reset -> success
    const currentStep = ref('email');

    // Email step
    const email = ref('');

    // Verify step
    const verificationCode = ref('');
    const resendCountdown = ref(0);
    const resetToken = ref('');

    // Reset password step
    const newPassword = ref('');
    const confirmPassword = ref('');
    const showPassword = ref(false);
    const showConfirmPassword = ref(false);

    // Handle email submission
    const handleSubmitEmail = async () => {
      // Reset errors
      formError.value = '';
      errors.value.email = '';

      // Basic validation
      if (!email.value.trim()) {
        errors.value.email = 'Email address is required';
        return;
      } else if (!/\S+@\S+\.\S+/.test(email.value)) {
        errors.value.email = 'Please enter a valid email address';
        return;
      }

      loading.value = true;

      try {
        // Call the backend API to send a reset code
        await apiClient.post('/auth/password/reset/request/', {
          email: email.value
        });

        // Move to verification step
        currentStep.value = 'verify';

        // Start resend countdown
        startResendCountdown();
      } catch (error) {
        if (error.response) {
          if (error.response.status === 404) {
            formError.value = 'No account found with this email address';
          } else if (error.response.data && error.response.data.error) {
            formError.value = error.response.data.error;
          } else {
            formError.value = 'An error occurred. Please try again.';
          }
        } else {
          formError.value = 'Network error. Please check your connection.';
        }
      } finally {
        loading.value = false;
      }
    };

    // Handle code verification
    const handleVerifyCode = async () => {
      // Reset errors
      formError.value = '';
      errors.value.code = '';

      // Basic validation
      if (!verificationCode.value.trim()) {
        errors.value.code = 'Verification code is required';
        return;
      }

      loading.value = true;

      try {
        // Call the backend API to verify the code
        const response = await apiClient.post('/auth/password/reset/verify/', {
          email: email.value,
          code: verificationCode.value
        });

        // Save the reset token for the next step
        resetToken.value = response.data.token;

        // Move to reset password step
        currentStep.value = 'reset';
      } catch (error) {
        if (error.response) {
          if (error.response.status === 400) {
            errors.value.code = 'Invalid verification code';
          } else if (error.response.data && error.response.data.error) {
            formError.value = error.response.data.error;
          } else {
            formError.value = 'An error occurred. Please try again.';
          }
        } else {
          formError.value = 'Network error. Please check your connection.';
        }
      } finally {
        loading.value = false;
      }
    };

    // Handle resend code
    const handleResendCode = async () => {
      if (resendCountdown.value > 0) return;

      loading.value = true;

      try {
        // Call the backend API to resend the code
        await apiClient.post('/auth/password/reset/request/', {
          email: email.value
        });

        // Start the countdown again
        startResendCountdown();
      } catch (error) {
        if (error.response && error.response.data && error.response.data.error) {
          formError.value = error.response.data.error;
        } else {
          formError.value = 'Failed to resend code. Please try again.';
        }
      } finally {
        loading.value = false;
      }
    };

    // Handle password reset
    const handleResetPassword = async () => {
      // Reset errors
      formError.value = '';
      errors.value.newPassword = '';
      errors.value.confirmPassword = '';

      // Basic validation
      let isValid = true;

      if (!newPassword.value) {
        errors.value.newPassword = 'New password is required';
        isValid = false;
      } else if (newPassword.value.length < 8) {
        errors.value.newPassword = 'Password must be at least 8 characters';
        isValid = false;
      } else if (!/[A-Z]/.test(newPassword.value)) {
        errors.value.newPassword = 'Password must include at least one uppercase letter';
        isValid = false;
      } else if (!/[0-9]/.test(newPassword.value)) {
        errors.value.newPassword = 'Password must include at least one number';
        isValid = false;
      }

      if (!confirmPassword.value) {
        errors.value.confirmPassword = 'Please confirm your new password';
        isValid = false;
      } else if (newPassword.value !== confirmPassword.value) {
        errors.value.confirmPassword = 'Passwords do not match';
        isValid = false;
      }

      if (!isValid) return;

      loading.value = true;

      try {
        // Call the backend API to reset the password
        await apiClient.post('/auth/password/reset/confirm/', {
          token: resetToken.value,
          new_password: newPassword.value,
          confirm_password: confirmPassword.value
        });

        // Move to success step
        currentStep.value = 'success';
      } catch (error) {
        if (error.response) {
          if (error.response.status === 400) {
            if (error.response.data.new_password) {
              errors.value.newPassword = error.response.data.new_password[0];
            } else if (error.response.data.confirm_password) {
              errors.value.confirmPassword = error.response.data.confirm_password[0];
            } else if (error.response.data.error) {
              formError.value = error.response.data.error;
            } else {
              formError.value = 'An error occurred. Please try again.';
            }
          } else {
            formError.value = 'An error occurred. Please try again.';
          }
        } else {
          formError.value = 'Network error. Please check your connection.';
        }
      } finally {
        loading.value = false;
      }
    };

    // Start resend countdown timer
    const startResendCountdown = () => {
      resendCountdown.value = 60; // 60 seconds countdown

      const timer = setInterval(() => {
        resendCountdown.value -= 1;

        if (resendCountdown.value <= 0) {
          clearInterval(timer);
        }
      }, 1000);
    };

    return {
      // Form state
      loading,
      formError,
      errors,
      currentStep,

      // Email step
      email,
      handleSubmitEmail,

      // Verify step
      verificationCode,
      resendCountdown,
      handleVerifyCode,
      handleResendCode,

      // Reset password step
      newPassword,
      confirmPassword,
      showPassword,
      showConfirmPassword,
      handleResetPassword,
    };
  }
};
</script>
